import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Activity, Users, Shield, Search, Layers } from 'lucide-react';
import AboutUsModal from './AboutUsModal';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isSolutionsExpanded, setIsSolutionsExpanded] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
    setIsSolutionsExpanded(false);
  };

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [isOpen]);

  return (
    <nav className="fixed w-full bg-white z-50 border-b border-gray-100 shadow-sm animate-slide-down">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group" onClick={closeMenu}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '28px',
              fontWeight: '500',
              position: 'relative',
            }}>
              {/* Circular Arrow Icon forming "O" */}
              <div style={{
                width: '28px',
                height: '28px',
                position: 'relative',
                flexShrink: 0,
                marginRight: '4px',
                transition: 'transform 0.3s ease',
              }} className="group-hover:scale-110 group-hover:rotate-12 group-hover:drop-shadow-lg">
                <svg viewBox="0 0 50 50" style={{
                  width: '100%',
                  height: '100%',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 188, 212, 0.3))',
                }}>
                  <defs>
                    <linearGradient id="navLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#00BCD4', stopOpacity: 1 }} />
                      <stop offset="50%" style={{ stopColor: '#0891B2', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#2563EB', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <g>
                    <path
                      fill="url(#navLogoGradient)"
                      stroke="url(#navLogoGradient)"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="
                      M 11.2,22.6
                      L 3.3,21.2
                      A 22 22 0 0 1 44.1,14.0
                      L 47.5,12.0
                      L 42.7,21.9
                      L 33.7,20.0
                      L 37.1,18.0
                      A 14 14 0 0 0 11.2,22.6
                      Z" />

                    <path
                      fill="url(#navLogoGradient)"
                      stroke="url(#navLogoGradient)"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="
                      M 38.8,27.4
                      L 46.7,28.8
                      A 22 22 0 0 1 6.0,36.0
                      L 2.5,38.0
                      L 7.3,28.1
                      L 16.3,30.0
                      L 12.9,32.0
                      A 14 14 0 0 0 38.8,27.4
                      Z" />
                  </g>
                </svg>
              </div>

              {/* Text part "rdinaa" */}
              <span style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
                color: '#06B6D4',
              }}>rdinaa</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`${isActive('/') ? 'text-cyan-600 font-medium border-b-2 border-cyan-600 pb-1' : 'text-gray-600 hover:text-cyan-600 border-b-2 border-transparent hover:border-cyan-400'} transition-all duration-300`}
            >
              Home
            </Link>

            <div className="relative group">
              <button
                className={`flex items-center gap-1 ${location.pathname.includes('/products') ? 'text-cyan-600 font-medium' : 'text-gray-600 hover:text-cyan-600'} transition-all duration-300 focus:outline-none`}
                onMouseEnter={() => setIsProductOpen(true)}
                onMouseLeave={() => setIsProductOpen(false)}
              >
                Solutions <ChevronDown size={16} className={`transition-transform duration-300 ${isProductOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown - Creative Grid Layout */}
              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 w-96 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-cyan-200/50 p-6 transition-all duration-300 transform origin-top ${isProductOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`}
                onMouseEnter={() => setIsProductOpen(true)}
                onMouseLeave={() => setIsProductOpen(false)}
              >
                <div className="space-y-3">
                  {/* Finance Cards - 2 Column */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* LendSmart */}
                    <Link to="/products/lendsmart" className="group/item relative p-4 rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600 border border-blue-400/40 hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity"></div>
                      <div className="relative">
                        <div className="p-2 bg-blue-400/30 rounded-lg mb-2 w-fit group-hover/item:bg-blue-300/50 transition-colors">
                          <Activity size={20} className="text-white" />
                        </div>
                        <h4 className="font-bold text-white text-xs">LendSmart AI</h4>
                        <p className="text-xs text-blue-100 mt-0.5">AI for Finance</p>
                      </div>
                    </Link>

                    {/* DeepFlow */}
                    <Link to="/products/deepflow" className="group/item relative p-4 rounded-xl bg-gradient-to-br from-rose-500 via-pink-500 to-rose-600 border border-rose-400/40 hover:border-rose-300 hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity"></div>
                      <div className="relative">
                        <div className="p-2 bg-rose-400/30 rounded-lg mb-2 w-fit group-hover/item:bg-rose-300/50 transition-colors">
                          <Layers size={20} className="text-white" />
                        </div>
                        <h4 className="font-bold text-white text-xs">DeepFlow AI</h4>
                        <p className="text-xs text-rose-100 mt-0.5">AI for Finance</p>
                      </div>
                    </Link>
                  </div>

                  {/* Other Solutions - 2 Column */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {/* Niyukti */}
                    <Link to="/products/niyukti" className="group/item relative p-4 rounded-xl bg-gradient-to-br from-cyan-500 via-teal-500 to-cyan-600 border border-cyan-400/40 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity"></div>
                      <div className="relative">
                        <div className="p-2 bg-cyan-400/30 rounded-lg mb-2 w-fit group-hover/item:bg-cyan-300/50 transition-colors">
                          <Users size={20} className="text-white" />
                        </div>
                        <p className="font-bold text-white text-xs">Niyukti AI</p>
                        <h4 className="text-xs text-cyan-100 mt-1">AI for Recruitment</h4>
                      </div>
                    </Link>

                    {/* TenderLens */}
                    <Link to="/products/tenderlens" className="group/item relative p-4 rounded-xl bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 border border-orange-400/40 hover:border-orange-300 hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity"></div>
                      <div className="relative">
                        <div className="p-2 bg-orange-400/30 rounded-lg mb-2 w-fit group-hover/item:bg-orange-300/50 transition-colors">
                          <Search size={20} className="text-white" />
                        </div>
                        <p className="font-bold text-white text-xs">TenderLens AI</p>
                        <h4 className="text-xs text-orange-100 mt-1">AI for Tenders</h4>
                      </div>
                    </Link>
                  </div>

                  {/* PQC Migration - Centered Below */}
                  <div className="pt-1 flex justify-center">
                    <Link to="/products/qsm" className="group/item relative p-4 rounded-xl bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 border border-purple-400/40 hover:border-purple-300 hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden w-[calc(50%-6px)]">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity"></div>
                      <div className="relative">
                        <div className="p-2 bg-purple-400/30 rounded-lg mb-2 w-fit group-hover/item:bg-purple-300/50 transition-colors">
                          <Shield size={20} className="text-white" />
                        </div>
                        <p className="font-bold text-white text-xs">PQC Migration</p>
                        <h4 className="text-xs text-purple-100 mt-1">AI for Security</h4>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/blog"
              className={`${isActive('/blog') ? 'text-cyan-600 font-medium border-b-2 border-cyan-600 pb-1' : 'text-gray-600 hover:text-cyan-600 border-b-2 border-transparent hover:border-cyan-400'} transition-all duration-300`}
            >
              Blog
            </Link>

            <Link
              to="/contact"
              className={`${isActive('/contact') ? 'text-cyan-600 font-medium border-b-2 border-cyan-600 pb-1' : 'text-gray-600 hover:text-cyan-600 border-b-2 border-transparent hover:border-cyan-400'} transition-all duration-300`}
            >
              Contact
            </Link>

            <button
              onClick={() => setIsAboutOpen(true)}
              className="text-gray-600 hover:text-cyan-600 border-b-2 border-transparent hover:border-cyan-400 transition-all duration-300 pb-1"
            >
              About Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="text-gray-600 hover:text-gray-900 focus:outline-none p-2"
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu */}
      <div className={`md:hidden bg-white border-t border-gray-100 absolute w-full z-50 transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'}`} style={{ transformOrigin: 'top' }}>
        <div className="px-4 pt-2 pb-6 space-y-2 shadow-xl">
          <Link
            to="/"
            className={`block px-3 py-4 text-base font-medium rounded-lg ${isActive('/') ? 'text-cyan-600 bg-cyan-50' : 'text-gray-700 hover:text-cyan-600 hover:bg-gray-50'}`}
            onClick={closeMenu}
          >
            Home
          </Link>
          <div className="px-3 py-2">
            <button
              onClick={() => setIsSolutionsExpanded(!isSolutionsExpanded)}
              className="flex items-center justify-between w-full font-medium text-gray-500 mb-2"
              aria-expanded={isSolutionsExpanded}
            >
              <span>Solutions</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-300 ${isSolutionsExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            <div className={`space-y-2 pl-2 border-l-2 border-gray-100 overflow-hidden transition-all duration-300 ${isSolutionsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="font-medium text-gray-600 text-xs uppercase tracking-wide mt-2 mb-1">Finance</div>
              <Link
                to="/products/lendsmart"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                onClick={closeMenu}
              >
                <Activity size={18} /> LendSmart AI
              </Link>
              <Link
                to="/products/deepflow"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-gray-600 hover:text-rose-600 hover:bg-gray-50 rounded-lg"
                onClick={closeMenu}
              >
                <Layers size={18} /> DeepFlow AI
              </Link>
              <div className="font-medium text-gray-600 text-xs uppercase tracking-wide mt-3 mb-1">Other Solutions</div>
              <Link
                to="/products/niyukti"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-gray-50 rounded-lg"
                onClick={closeMenu}
              >
                <Users size={18} /> Niyukti AI
              </Link>
              <Link
                to="/products/tenderlens"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-gray-600 hover:text-amber-600 hover:bg-gray-50 rounded-lg"
                onClick={closeMenu}
              >
                <Search size={18} /> TenderLens AI
              </Link>
              <Link
                to="/products/qsm"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-gray-600 hover:text-purple-600 hover:bg-gray-50 rounded-lg"
                onClick={closeMenu}
              >
                <Shield size={18} /> PQC Migration
              </Link>
            </div>
          </div>
          <Link
            to="/blog"
            className={`block px-3 py-4 text-base font-medium rounded-lg ${isActive('/blog') ? 'text-cyan-600 bg-cyan-50' : 'text-gray-700 hover:text-cyan-600 hover:bg-gray-50'}`}
            onClick={closeMenu}
          >
            Blog
          </Link>
          <Link
            to="/contact"
            className={`block px-3 py-4 text-base font-medium rounded-lg ${isActive('/contact') ? 'text-cyan-600 bg-cyan-50' : 'text-gray-700 hover:text-cyan-600 hover:bg-gray-50'}`}
            onClick={closeMenu}
          >
            Contact
          </Link>
          <button
            onClick={() => {
              setIsAboutOpen(true);
              closeMenu();
            }}
            className="block w-full text-left px-3 py-4 text-base font-medium text-gray-700 hover:text-cyan-600 hover:bg-gray-50 rounded-lg"
          >
            About Us
          </button>
        </div>
      </div>

      <AboutUsModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
    </nav>
  );
};

export default Navbar;
